import { css } from '@linaria/atomic';
import { AiReceptionistEventType } from '@weave/schema-gen-ts/dist/shared/phone/events/vair/v1/vair.pb';
import { i18next } from '@frontend/i18n';
import { AiEventBadge, ChecklistChipMenuField } from '@frontend/phone-ui';
import { usePreferenceBasedRequestsAccess } from '@frontend/schedule-helpers';
import { ChecklistMenuField, useFormField } from '@frontend/design-system';
import { AI_EVENT_CONFIG } from './ai-events-widgets';

const callEvents = [
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_MISSED,
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_ANSWERED,
] as const;

const scheduleEvents = [
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_CANCELLED,
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_ADDED,
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_RESCHEDULED,
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_CONFIRMED,
] as const;

const scheduleRequestEvents = [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_REQUESTED] as const;

const fatalEvents = [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_FATAL] as const;

const separatorStyle = css`
  border-top: 1px solid var(--fabric-color-border-subtle);
  margin: var(--fabric-space-padding-xxs) 0;
  padding-top: var(--fabric-space-padding-xs);
`;

const floatingOverrideStyle = css`
  margin-top: var(--fabric-space-padding-xs);
  margin-bottom: var(--fabric-space-padding-xs);
`;

const renderAiEventOption = (eventType: AiReceptionistEventType, hasSeparator = false) => {
  const config = AI_EVENT_CONFIG[eventType];
  if (!config) {
    return null;
  }

  return (
    <ChecklistMenuField.Option
      key={eventType}
      textValue={config.text}
      value={String(eventType)}
      className={hasSeparator ? separatorStyle : undefined}
    >
      <AiEventBadge config={config} size='large' />
    </ChecklistMenuField.Option>
  );
};

type AiEventsChecklistFieldProps = {
  selectedEvents: AiReceptionistEventType[];
  onChange: (value: AiReceptionistEventType[]) => void;
  trackingId?: string;
  label?: string;
};

export const AiEventsChecklistField = ({
  selectedEvents,
  onChange,
  trackingId,
  label,
}: AiEventsChecklistFieldProps) => {
  const { hasPreferenceBasedRequestsAccess } = usePreferenceBasedRequestsAccess();

  const eventFieldProps = useFormField(
    {
      type: 'checklist',
      value: selectedEvents,
    },
    [selectedEvents]
  );

  const scheduleGroupEvents = hasPreferenceBasedRequestsAccess ? scheduleRequestEvents : scheduleEvents;

  return (
    <ChecklistChipMenuField
      fieldProps={eventFieldProps}
      label={label ?? i18next.t('Select AI Receptionist Outcomes', { ns: 'calls' })}
      name='ai-events'
      onChange={(nextValue) => {
        onChange(nextValue as AiReceptionistEventType[]);
      }}
      trackingId={trackingId}
      showSearchBar={false}
      showSelectionBar={false}
      floatingStyleOverrides={floatingOverrideStyle}
    >
      {callEvents.map((eventType) => renderAiEventOption(eventType))}
      {scheduleGroupEvents.map((eventType, index) => renderAiEventOption(eventType, index === 0))}
      {fatalEvents.map((eventType, index) => renderAiEventOption(eventType, index === 0))}
    </ChecklistChipMenuField>
  );
};
